import { Fragment, useId, useState } from "react";
import type { ReactNode } from "react";
import { Tag } from "@arco-design/web-react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { formatFeeAdjustmentAmount, formatFeeAdjustmentCompactAmount, getFeeAdjustmentServiceProject, getFeeAdjustmentSalesTotal, getFeeAdjustmentLineIntegerMarkupAmount } from "../service";
import { FeeAdjustmentDescription } from "./FeeAdjustmentDescription";
import type { FeeAdjustmentLineDisplay, FeeAdjustmentMember } from "../types";
import "./feeAdjustmentServiceTable.css";

/** 确认件增减、申请草稿与完成记录共用的服务明细；金额口径由入口明确指定。 */
export function FeeAdjustmentServiceTable({ lines, amountKind, label, renderActions, markupMembers = [], compactAmounts = false, showUnitPrice = true, groupDescriptionAmounts = false, formatAmount: customFormatAmount }: {
  lines: FeeAdjustmentLineDisplay[];
  amountKind: "cost" | "r3";
  label: string;
  renderActions?: (line: FeeAdjustmentLineDisplay) => ReactNode;
  markupMembers?: FeeAdjustmentMember[];
  compactAmounts?: boolean;
  showUnitPrice?: boolean;
  groupDescriptionAmounts?: boolean;
  formatAmount?: (value: string | null | undefined, signed?: boolean) => string;
}) {
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  const tableId = useId();
  const showMarkup = markupMembers.length > 0;
  const formatAmount = customFormatAmount || (compactAmounts ? formatFeeAdjustmentCompactAmount : formatFeeAdjustmentAmount);
  const columnCount = 3 + Number(showUnitPrice) + Number(showMarkup) + Number(Boolean(renderActions));
  return <div className="fee-service-table-wrap"><table className={`fee-service-table${showMarkup ? " fee-service-table--markup" : ""}${!showUnitPrice ? " fee-service-table--without-unit-price" : ""}`} aria-label={label}>
    <thead><tr><th>服务项目</th><th>调整明细</th><th>{amountKind === "r3" ? "调整成本" : "调整总价"}</th>{showUnitPrice && <th>调整单价</th>}{showMarkup && <th>加价合计</th>}{renderActions && <th>操作</th>}</tr></thead>
    <tbody>{lines.map((line) => {
      const amount = formatAmount(amountKind === "r3" ? line.r3_cost_amount : line.cost_delta_amount, true);
      const project = getFeeAdjustmentServiceProject(line);
      const markups = markupMembers.map((member) => {
        const amounts = line.sales_amount_by_member;
        const value = amounts && typeof amounts === "object"
          ? Object.hasOwn(amounts, member.roster_member_id) ? amounts[member.roster_member_id] : "0.00"
          : undefined;
        return { member, value };
      });
      const markup = formatAmount(getFeeAdjustmentSalesTotal(line), true);
      const expanded = expandedLines.has(line.id);
      const detailsId = `${tableId}-${line.id}`;
      return <Fragment key={line.id}><tr>
        <td><strong>{project.name}</strong>
          {project.spec && <span>{project.spec}</span>}
          {line.settlement_supplier_name_snapshot && <span>{line.settlement_supplier_name_snapshot}</span>}
        </td>
        <td><FeeAdjustmentDescription line={line} compactAmounts={compactAmounts} groupAmounts={groupDescriptionAmounts} formatAmount={customFormatAmount} /></td>
        <td className={amount.startsWith("+") ? "is-increase" : amount.startsWith("−") ? "is-decrease" : undefined}>{amount}</td>
        {showUnitPrice && <td>{formatAmount(line.after.unit_price_amount, false)}</td>}
        {showMarkup && <td className={markup.startsWith("+") ? "is-increase" : markup.startsWith("−") ? "is-decrease" : undefined}>
          <button type="button" className="fee-service-markup-toggle" aria-expanded={expanded} aria-controls={detailsId}
            aria-label={`${expanded ? "收起" : "展开"}${project.name}成员加价明细，合计${markup}`}
            onClick={() => setExpandedLines((current) => {
              const next = new Set(current);
              if (next.has(line.id)) next.delete(line.id); else next.add(line.id);
              return next;
            })}>
            {markup}{expanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          </button>
        </td>}
        {renderActions && <td>{renderActions(line)}</td>}
      </tr>
        {showMarkup && expanded && <tr className="fee-service-markup-expanded" id={detailsId}><td colSpan={columnCount}>
          <div className="fee-service-markup-heading">成员加价明细<span>{markups.length} 人</span></div>
          <table className="fee-service-markup-details" aria-label={`${project.name}成员加价明细`}>
            <thead><tr><th>成员</th><th>人员类型</th><th>是否出钱</th><th>加价金额</th><th>取整金额</th></tr></thead>
            <tbody>{markups.map(({ member, value }) => {
              const memberMarkup = formatAmount(value, true);
              const integerAmount = formatAmount(getFeeAdjustmentLineIntegerMarkupAmount(line, member), true);
              return <tr key={member.roster_member_id}>
                <td>{member.member_name_snapshot}</td>
                <td>{member.traveler_type_snapshot}</td>
                <td><Tag color={member.is_payer ? "blue" : "gray"}>{member.is_payer ? "出钱" : "不出钱"}</Tag></td>
                <td className={memberMarkup.startsWith("+") ? "is-increase" : memberMarkup.startsWith("−") ? "is-decrease" : undefined}>{memberMarkup}</td>
                <td className={integerAmount.startsWith("+") ? "is-increase" : integerAmount.startsWith("−") ? "is-decrease" : undefined}>{integerAmount}</td>
              </tr>;
            })}</tbody>
          </table>
        </td></tr>}
      </Fragment>;
    })}</tbody>
  </table></div>;
}
